import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar, IconButton, Typography, Box, Button, Portal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import { useSwipeable } from 'react-swipeable';
import {
  parseTicketFromUrl,
  resolveNotificationTarget,
  extractNotificationKind
} from '../services/notification-target';

// Compare a parsed ticket to the current route. Handles both the legacy
// /gestion?id=<id> form and the new /gestion/<id> form for gestiones, and
// /reporte?id=<id> for averias.
function isOnTicketPage(ticket) {
  if (!ticket) return false;
  const currentPath = window.location.pathname;
  const currentSearch = window.location.search;
  if (ticket.kind === 'gestion') {
    // LiveThread (the comentarios thread) only mounts on /gestion/<id>/chat,
    // so the snackbar is only suppressed there — on the /gestion/<id> detail
    // page (or anywhere else) the snackbar must still render so the user can
    // click into the thread.
    const mChat = currentPath.match(/^\/gestion\/(.+)\/chat$/);
    return !!mChat && String(mChat[1]) === ticket.id;
  }
  if (ticket.kind === 'issue') {
    if (currentPath !== '/reporte') return false;
    return String(new URLSearchParams(currentSearch).get('id')) === ticket.id;
  }
  return false;
}

const InAppNotification = () => {
  const [notification, setNotification] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (event) => {
      if (!event.data) return;

      if (event.data.type === 'NOTIFICATION_CLICK') {
        // Navigate to the notification URL — must be same-origin relative for
        // react-router's navigate() to work. The service worker broadcasts an
        // ABSOLUTE url, so parse it first (mirrors App.js:268-287). Apply
        // kind-aware resolution too so an outdated SW can't bypass the
        // gestion_comment → /gestion/<id>/chat rewrite.
        const url = event.data.url || (event.data.notification?.data?.url);
        const kind = extractNotificationKind(event.data?.notification?.data);
        const rel = resolveNotificationTarget({ url, kind });
        if (rel) {
          navigate(rel);
        }
      } else if (event.data.type === 'PUSH_RECEIVED') {
        const notif = event.data.notification || {};
        const data = notif.data || {};
        // data is the SW buildNotificationData wrapper
        // {url, data: rawFcmData, notification, fcmOptions, FCM_MSG} — kind
        // and action_name live inside the nested raw FCM data.
        const innerData = data.data || (data.FCM_MSG && data.FCM_MSG.data) || {};
        setNotification({
          title: notif.title || 'MiGarita.Online',
          body: notif.body || '',
          url: data.url || '/',
          action_name: data.action_name || innerData.action_name,
          kind: extractNotificationKind(data)
        });
        setOpen(true);
      }
    };

    try {
      navigator.serviceWorker.addEventListener('message', handler);
      return () => navigator.serviceWorker.removeEventListener('message', handler);
    } catch (error) {
      console.error('Error adding service worker event listener:', error);
    }
  }, [navigate]);

  useEffect(() => {
    const handlePush = (event) => {
      const detail = event.detail || {};
      const url = detail.url || '/';

      // Focus-if-open: if the user is already on the matching ticket page,
      // suppress the snackbar and instead ask LiveThread to focus the newest
      // comment (the websocket already delivered it).
      const ticket = parseTicketFromUrl(url);
      if (ticket && isOnTicketPage(ticket)) {
        window.dispatchEvent(new CustomEvent('mgo:focus-latest-comment', {
          detail: { kind: ticket.kind, id: ticket.id }
        }));
        return;
      }

      setNotification({
        title: detail.title || 'MiGarita.Online',
        body: detail.body || '',
        url: url,
        action_name: detail.action_name,
        kind: detail.kind
      });
      setOpen(true);
    };

    window.addEventListener('mgo:push-received', handlePush);
    return () => window.removeEventListener('mgo:push-received', handlePush);
  }, []);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleExited = () => {
    setNotification(null);
  };

  const handleClick = () => {
    if (notification && notification.url) {
      const rel = resolveNotificationTarget(notification);
      if (rel) {
        navigate(rel);
        setOpen(false);
      }
    }
  };

  const handleActionClick = () => {
    if (notification && notification.url) {
      const rel = resolveNotificationTarget(notification);
      if (rel) {
        navigate(rel);
        setOpen(false);
      }
    }
  };

  const handleSwipe = (eventData) => {
    if (eventData.dir === 'Left' || eventData.dir === 'Right' || eventData.dir === 'Up') {
      setOpen(false);
    }
  };

  const swipeHandlers = useSwipeable({
    onSwiped: handleSwipe,
    trackMouse: true
  });

  return (
    <Portal>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "down", onExited: handleExited }}
        sx={{ cursor: 'pointer', zIndex: 1500 }}
      >
        <Box
          {...swipeHandlers}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: '10px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            padding: 2,
            minWidth: '400px',
            maxWidth: '95%',
            display: 'flex',
            flexDirection: 'column',
            touchAction: 'pan-y',
          }}
          onClick={handleClick}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box
              component="img"
              src="/logo.svg"
              alt="App Logo"
              sx={{
                width: 24,
                height: 24,
                mr: 1
              }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
              {notification?.title}
            </Typography>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ mb: notification?.action_name ? 1 : 0 }}>
            {notification?.body}
          </Typography>
          {notification?.action_name && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleActionClick}
              sx={{ alignSelf: 'flex-end', mt: 1 }}
            >
              {notification.action_name}
            </Button>
          )}
        </Box>
      </Snackbar>
    </Portal>
  );
};

export default InAppNotification;
